import { ApiLine } from "griptonite-api-client";
import { useMediaQuery } from "react-responsive";
import { useShallow } from "zustand/react/shallow";

import roundedSquareDashes from "@/utils/roundedSquareDashes";

import { useOnDragStart } from "../../hooks/useOnDragStart";
import { useRoutesUiStore } from "../RoutesProvider";
import MapRoutePinSvg from "./MapRoutePinSvg";
import { LineRoutes } from "./MapsList";

export type Props = {
  onClick: () => void;
  isHighlighted?: boolean | null;
  onContextMenu?: (e: React.MouseEvent, uuid: string) => void;
  lineRoutes?: LineRoutes;
  line?: ApiLine;
  dragToMoveEnabled?: boolean;
  onDragStart?: () => void;
  selectedRouteUuids?: Set<string>;
  filteredRouteUuids?: Set<string>;
  /** Extra scale for the marker, or for each route pin when shown as a stack. */
  routePinScale?: number;
  /**
   * How many times larger than normal the marker appears on screen at the current zoom.
   * Up to MAX_OUTLINE_MAGNIFICATION, the outline's stroke widths and dash spacing are divided
   * by this, so they stay the same on screen while the marker grows.
   */
  screenMagnification?: number;
};

const PIN_WIDTH = 14;
const CORNER_RADIUS = 3;
// The outline has this many dashes at normal size.
const DASH_COUNT = 16;
const MAX_OUTLINE_MAGNIFICATION = 2;

const allRoutesFilteredOut = (lineRoutes?: LineRoutes, filteredRouteUuids?: Set<string>) =>
  !!filteredRouteUuids?.size &&
  !!lineRoutes?.routes.every((route) => route.uuid && !filteredRouteUuids.has(route.uuid));

/**
 * A line's marker, shown instead of its route pins, for example when it has no routes. It's
 * a hollow, dashed slot, so it can't be mistaken for a route pin of any colour. White dashes
 * over a dark outline keep it visible on both the floor and the white walls.
 */
export default function MapLinePinSvg({
  lineRoutes,
  line,
  onClick,
  isHighlighted,
  onContextMenu,
  dragToMoveEnabled = false,
  onDragStart,
  selectedRouteUuids,
  filteredRouteUuids,
  routePinScale = 1,
  screenMagnification = 1,
}: Props) {
  const isTouch = useMediaQuery({ query: "(pointer: coarse)" });
  const dragCallbacks = useOnDragStart(onDragStart || (() => {}));

  const lineUuid = (line || lineRoutes?.line)?.uuid;
  if (!lineUuid) {
    throw new Error("Line or lineRoutes must be provided");
  }

  const highlightScale = isHighlighted === true ? 1.2 : isHighlighted === false ? 0.8 : 1;
  const hasVisibleSelectedRoute =
    !!lineRoutes?.routes.some((route) => selectedRouteUuids?.has(route.uuid || "")) &&
    !allRoutesFilteredOut(lineRoutes, filteredRouteUuids);
  const isDimmed = !!selectedRouteUuids?.size && !hasVisibleSelectedRoute;

  const outlineMagnification = Math.min(screenMagnification, MAX_OUTLINE_MAGNIFICATION);
  // Keep a multiple of 4 so all four sides and corners look the same.
  const dashCount = 4 * Math.round((DASH_COUNT * outlineMagnification) / 4);

  return (
    <g
      transform={`scale(${routePinScale * highlightScale})`}
      style={{ cursor: dragToMoveEnabled ? "move" : "pointer" }}
      onClick={onClick}
      onContextMenu={onContextMenu && ((e) => onContextMenu(e, lineUuid))}
      {...(dragToMoveEnabled ? dragCallbacks : {})}
    >
      {isTouch && <circle cx={0} cy={0} r={PIN_WIDTH / 2 + 10} fill="transparent" />}
      <g opacity={isDimmed ? 0.5 : 1}>
        <rect
          x={-PIN_WIDTH / 2}
          y={-PIN_WIDTH / 2}
          width={PIN_WIDTH}
          height={PIN_WIDTH}
          rx={CORNER_RADIUS}
          fill="black"
          fillOpacity={0.1}
          stroke={isHighlighted ? "#0075FF" : "#1A1A1A"}
          strokeOpacity={isHighlighted ? 1 : 0.6}
          strokeWidth={4 / outlineMagnification}
        />
        {!isHighlighted && (
          <path
            d={roundedSquareDashes(PIN_WIDTH, CORNER_RADIUS, dashCount)}
            fill="none"
            stroke="white"
            strokeWidth={1 / outlineMagnification}
          />
        )}
      </g>
    </g>
  );
}

export const MapLineRoutePinStack = ({
  lineRoutes,
  onClick,
  onContextMenu,
  selectedRouteUuids,
  filteredRouteUuids,
  routePinScale = 1,
}: {
  onClick: () => void;
  isHighlighted?: boolean | null;
  onContextMenu?: (e: React.MouseEvent, uuid: string) => void;
  lineRoutes: LineRoutes;
  selectedRouteUuids: Set<string>;
  filteredRouteUuids: Set<string>;
  /** Extra scale for each pin, leaving the stack's offsets unchanged. */
  routePinScale?: number;
}) => {
  const overlapOffset = 2;
  // const dragHandlers = useOnDragStart((el) => {
  //   const route = lineRoutes.routes.find(route => route.uuid === el.dataset.uuid);
  //   if (!route || !route.) {
  //     return;
  //   }
  // })
  // if ()
  return (
    <g
      transform="scale(1)"
      style={{ cursor: "pointer" }}
      onClick={onClick}
      onContextMenu={onContextMenu && ((e) => onContextMenu(e, lineRoutes.line.uuid))}
    >
      {lineRoutes.routes
        .slice()
        .reverse()
        .map((route, i) => {
          const offsetPx = (i - lineRoutes.routes.length / 2) * overlapOffset;
          return (
            <g
              transform={`translate(${offsetPx}, ${offsetPx})`}
              key={route.uuid}
              opacity={
                (selectedRouteUuids.size === 0 || selectedRouteUuids.has(route.uuid || "")) &&
                (!filteredRouteUuids.size ||
                  !route.uuid ||
                  filteredRouteUuids.has(route.uuid || ""))
                  ? 1
                  : 0.5
              }
            >
              <g transform={`scale(${routePinScale})`}>
                <MapRoutePinSvg route={route} hideText={i != lineRoutes.routes.length - 1} />
              </g>
            </g>
          );
        })}
    </g>
  );
};

export const MapLineIndicator = (
  props: Props & {
    selectedRouteUuids: Set<string>;
    filteredRouteUuids: Set<string>;
  },
) => {
  const { currentlyDraggingRouteUuid, highlightedLineUuid } = useRoutesUiStore(
    useShallow((state) => ({
      currentlyDraggingRouteUuid: state.currentlyDraggingRouteUuid,
      highlightedLineUuid: state.highlightedLineUuid,
    })),
  );
  const line = props.line || props.lineRoutes?.line;
  const lineRoutes = props.lineRoutes;
  if (!line || !lineRoutes) {
    throw new Error("Line or lineRoutes must be provided");
  }
  let isHighlighted = null;
  if (highlightedLineUuid) {
    isHighlighted = line.uuid === highlightedLineUuid;
  }

  if (currentlyDraggingRouteUuid || allRoutesFilteredOut(lineRoutes, props.filteredRouteUuids)) {
    return (
      <MapLinePinSvg
        {...props}
        isHighlighted={isHighlighted}
        selectedRouteUuids={currentlyDraggingRouteUuid ? new Set() : props.selectedRouteUuids}
      />
    );
  }
  return <MapLineRoutePinStack {...props} lineRoutes={lineRoutes} />;
};
